<header class="content-header">
  <h4 class="vision-h4">{{ 'createTaskDialog.labels.createTasks' | translate }}</h4>
  <div class="project-context" *ngIf="data.project_id && includesTagsContainer">
    <span test-id="project-context">{{ 'common.labels.project' | translate | titlecase }}: {{ includesTagsContainer.label }}</span>
  </div>
</header>

<div mat-dialog-content>
  <form [formGroup]="formGroup" class="create-group-form">
    <div class="task-criteria" *ngIf="!data.project_id && currentUser">
      <label [attr.aria-label]="'createTaskDialog.labels.selectProject' | translate">
        {{ 'createTaskDialog.labels.selectProject' | translate }}
      </label>
      <div class="mat-form-field">
        <project-picker #projectPicker
          test-id="project-picker"
          [filter]="getProjectsFilter()"
          (valueChange)="selectProject($event)"
          [validators]="[requiredValidator, projectHasDataValidator]"
        >
          <mat-error *ngIf="projectPicker.touched && projectPicker.errors?.required">
            {{ 'createTaskDialog.error.projectRequired' | translate }}
          </mat-error>
          <mat-error *ngIf="projectPicker.touched && projectPicker.errors?.empty">
            {{ projectPicker.errors.empty }}
          </mat-error>
        </project-picker>
      </div>
    </div>

    <div class="task-criteria">
      <label [attr.aria-label]="'createTaskDialog.labels.taskType' | translate">
        {{ 'createTaskDialog.labels.taskType' | translate }}
      </label>
      <mat-form-field appearance="outline">
        <mat-select test-id="task-type" formControlName="taskType">
          <mat-option *ngFor="let taskType of taskTypes" [value]="taskType.value">{{ taskType.label }}</mat-option>
        </mat-select>
        <mat-error *ngIf="formGroup.controls.taskType.touched && formGroup.controls.taskType.hasError('required')">{{
          'createTaskDialog.error.taskTypeRequired' | translate }}</mat-error>
      </mat-form-field>
    </div>

    <!-- Read protocols only display for "Reader" task type -->
    <div class="task-criteria" *ngIf="formGroup.controls.taskType.value === 'R' && protocolOptions">
      <label [attr.aria-label]="'createTaskDialog.labels.readProtocol' | translate">
        {{ 'createTaskDialog.labels.readProtocol' | translate }}
      </label>
      <mat-form-field appearance="outline">
        <mat-select test-id="read-protocol" formControlName="readProtocol" [placeholder]="'createTaskDialog.labels.readProtocolPlaceholder' | translate">
          <mat-option *ngFor="let protocol of protocolOptions" [value]="protocol.value">{{ protocol.label }}</mat-option>
        </mat-select>
        <mat-error *ngIf="formGroup.controls.readProtocol.hasError('required')">{{ 'createTaskDialog.error.readProtocolRequired' | translate }}</mat-error>
      </mat-form-field>
      <div class="read-protocol-warning" *ngIf="protocolOptions.length === 0">
        <ng-container *ngIf="getProjectLabel(formGroup.controls.project.value) as projectLabel; else noProjectForProtocols">
          <mat-icon class="xs orange">warning</mat-icon><i>{{ 'createTaskDialog.labels.readProtocolNotFoundForProject' | translate | interpolate:projectLabel }}</i>
        </ng-container>
        <ng-template #noProjectForProtocols>
          <ng-container *ngIf="projectWasTouched()">
            <mat-icon class="xs orange">warning</mat-icon><i>{{ 'createTaskDialog.labels.readProtocolNotFound' | translate }}</i>
          </ng-container>
        </ng-template>
      </div>
    </div>

    <div class="task-criteria">
      <label [attr.aria-label]="'common.labels.container' | translate | titlecase">
        {{ 'common.labels.container' | translate | titlecase }}
      </label>
      <mat-form-field appearance="outline">
        <mat-select test-id="fw-object-select" formControlName="container"
          [placeholder]="'createTaskDialog.labels.containerPlaceholder' | translate">
          <mat-option *ngFor="let container of containerTypes" [value]="container.value">{{ container.label }}</mat-option>
        </mat-select>
        <mat-error *ngIf="formGroup.controls.container.hasError('required')">{{ 'createTaskDialog.error.containerRequired' |
          translate }}</mat-error>
      </mat-form-field>
    </div>

    <div class="full-width" *ngIf="includesTagsContainer">
      <label [attr.aria-label]="'createTaskDialog.labels.containerFilter' | translate">
        {{ 'createTaskDialog.labels.containerFilter' | translate }}
      </label>
      <div>
        <mat-label>{{ 'createTaskDialog.labels.containsTags' | translate }}</mat-label>
        <fw-tags
          test-id="container-includes-tags"
          [useAsInlineField]="true"
          formControlName="containerIncludesTags"
          [container]="includesTagsContainer"
          [autoSave]="false">
        </fw-tags>
      </div>
      <div>
        <mat-label>{{ 'createTaskDialog.labels.notContainsTags' | translate }} </mat-label>
        <fw-tags test-id="container-excludes-tags" [useAsInlineField]="true" formControlName="containerExcludesTags"
          [container]="excludesTagsContainer" [autoSave]="false">
        </fw-tags>
      </div>
    </div>

    <div class="container-count full-width" *ngIf="shouldFetchContainerCount$ | async">
      <ng-container *ngIf="!loadingMatchingContainers; else loadingContainerCount">
        <strong class="blue" style="font-size: 16px;">{{ matchingContainerCount }}</strong>
        <span>{{ selectedContainerLabel }} {{ 'createTaskDialog.labels.found' | translate }}</span>
      </ng-container>
      <ng-template #loadingContainerCount>
        <mat-spinner diameter="16"></mat-spinner>
        <span>{{ 'createTaskDialog.labels.searchingFor' | translate }} {{ selectedContainerLabel | lowercase }}</span>
      </ng-template>
    </div>

    <div class="task-criteria">
      <div class="flex-label">
        <label [attr.aria-label]="'common.labels.assignee' | translate">
          {{ 'common.labels.assignee' | translate }}
        </label>
        <mat-icon matTooltip="{{ 'createTaskDialog.labels.assigneeInfo' | translate }}" class="xs">info</mat-icon>
      </div>
      <div class="mat-form-field">
        <app-typeahead
          #assigneeTypeahead
          test-id="user-typeahead"
          class="solid-background full-width"
          [disabled]="formGroup.get('assignee').disabled"
          [placeholder]="'common.labels.assignee' | translate"
          [options]="userOptions"
          [validators]="[requiredAssigneeValidator, noAssigneesValidator]"
          (valueChange)="updateAssignee($event.toString())">
        </app-typeahead>
      </div>
    </div>

    <div class="task-criteria">
      <label [attr.aria-label]="'createTaskDialog.labels.dueDate' | translate">
        {{ 'createTaskDialog.labels.dueDate' | translate }}
      </label>
      <mat-form-field appearance="outline" class="date-field flex-field">
        <input test-id="due-date-input" formControlName="dueDate" matInput [matDatepicker]="datepicker"
          [placeholder]="'createTaskDialog.labels.selectDate' | translate" autocomplete="off">
        <mat-datepicker-toggle [for]="datepicker"></mat-datepicker-toggle>
        <mat-datepicker #datepicker></mat-datepicker>
        <mat-error *ngIf="formGroup.controls.dueDate.hasError('required')">{{ 'createTaskDialog.error.dueDateRequired' | translate }}</mat-error>
        <mat-error *ngIf="formGroup.controls.dueDate.hasError('dateIsPast')">{{ 'createTaskDialog.error.dueDatePast' | translate }}</mat-error>
      </mat-form-field>
    </div>

    <div class="full-width" *ngIf="taskTagsContainer">
      <label>{{ 'createTaskDialog.labels.taskTags' | translate }}</label>
      <fw-tags
        [allowCustomTags]="true"
        [useAsInlineField]="true"
        formControlName="tags"
        [container]="taskTagsContainer"
        [autoSave]="false">
      </fw-tags>
    </div>
  </form>
</div>

<div class="action-buttons" mat-dialog-actions align="end">
  <div class="footer-create-more">
    <mat-checkbox test-id="create-more-checkbox" [(ngModel)]="createMore"></mat-checkbox>
    <span>{{ 'createTaskDialog.actions.createMore' | translate }}</span>
  </div>
  <button test-id="close-button" mat-stroked-button (click)="cancel()">{{ 'common.actions.cancel' | translate }}</button>
  <button test-id="create-button" mat-flat-button color="primary" (click)="submit()"
    [disabled]="formGroup.invalid">
    {{ 'common.actions.create' | translate }}
  </button>
</div>
